import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, EventEmitter, Component, ViewChild, Output, Input, HostListener, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { __awaiter } from 'tslib';
import { Subject, of, lastValueFrom, BehaviorSubject } from 'rxjs';
import * as i1 from '@cisstech/nge/services';
import { map } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import { CommonModule } from '@angular/common';

/**
 * Monaco editor loader configuration token.
 */
const NGE_MONACO_CONFIG = new InjectionToken('NGE_MONACO_CONFIG');

const NGE_MONACO_CONTRIBUTION = new InjectionToken('NGE_MONACO_CONTRIBUTION');

/** monaco editor cdn url hosted at cdnjs. */
const MONACO_CDNJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.31.1';
/** monaco editor cdn url hosted at jsdeliver. */
const MONACO_JS_DELIVER_URL = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.31.1';
const WINDOW = window;
/**
 * Loads monaco editor using AMD loader.
 */
class NgeMonacoLoaderService {
    constructor(config, contributions, resourceLoader) {
        this.config = config;
        this.contributions = contributions;
        this.resourceLoader = resourceLoader;
        this.monaco$ = new Subject();
        this.baseUrl = MONACO_CDNJS_URL;
        this.contributions = contributions || [];
    }
    ngOnDestroy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.deactivateContributions();
        });
    }
    /**
     * Call the given `observer` function to extend monaco editor functionalities
     * once it will be available in `window.monaco`.
     *
     * The function will be called immediately if monaco api is already loaded.
     * @param observer observer object.
     * @returns A subscription object that should be unsubscribed later.
     */
    onLoadMonaco(observer) {
        if (typeof WINDOW.monaco === 'undefined') {
            return this.monaco$.asObservable().subscribe(observer);
        }
        return of(WINDOW.monaco).subscribe(observer);
    }
    /**
     * Loads monaco editor if it is not loaded.
     */
    loadAsync() {
        var _a;
        return (_a = this.loadPromise) !== null && _a !== void 0 ? _a : (this.loadPromise = new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
            // Try to fix the issues described here by loading monaco editor
            // after all the other scripts.
            // https://stackoverflow.com/a/33635881
            // https://github.com/microsoft/monaco-editor/issues/662
            // https://github.com/microsoft/monaco-editor/issues/1249
            const interval = setInterval(() => {
                if (document.readyState !== 'complete')
                    return;
                clearInterval(interval);
                setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    yield this.resourceLoader.waitForPendings();
                    this.baseUrl = ((_a = this.config) === null || _a === void 0 ? void 0 : _a.assets) || MONACO_CDNJS_URL;
                    if (this.baseUrl.endsWith('/')) {
                        this.baseUrl = this.baseUrl.slice(0, this.baseUrl.length - 1);
                    }
                    this.addWorkersIfCrossDomain();
                    if (!WINDOW.require) {
                        lastValueFrom(this.resourceLoader.loadAllAsync([
                            ['script', `${this.baseUrl}/min/vs/loader.js`]
                        ]))
                            .then(() => this.onLoad(resolve));
                    }
                    else {
                        this.onLoad(resolve);
                    }
                }), 300);
            });
        })));
    }
    onLoad(resolve) {
        var _a;
        WINDOW.require.config({
            paths: { vs: this.baseUrl + '/min/vs' }
        });
        const locale = ((_a = this.config) === null || _a === void 0 ? void 0 : _a.locale) || '';
        if (locale !== 'en') {
            WINDOW.require.config({
                'vs/nls': {
                    availableLanguages: { '*': locale },
                }
            });
        }
        WINDOW.require(['vs/editor/editor.main'], () => __awaiter(this, void 0, void 0, function* () {
            yield this.activateContributions();
            this.monaco$.next(monaco);
            resolve(monaco);
        }));
    }
    addWorkersIfCrossDomain() {
        // https://github.com/microsoft/monaco-editor/blob/master/docs/integrate-amd-cross.md
        if (this.baseUrl.startsWith('http')) {
            const proxy = URL.createObjectURL(new Blob([`
                self.MonacoEnvironment = { baseUrl: '${this.baseUrl}/min' };
                importScripts('${this.baseUrl}/min/vs/base/worker/workerMain.js');
            `], { type: 'text/javascript' }));
            WINDOW.MonacoEnvironment = {
                baseUrl: this.baseUrl + '/min',
                getWorkerUrl: () => proxy,
                globalAPI: true,
            };
        }
    }
    activateContributions() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.contributions.map(e => e.activate()));
        });
    }
    deactivateContributions() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(this.contributions.map(e => {
                if (e.deactivate) {
                    return e.deactivate();
                }
                return Promise.resolve();
            }));
        });
    }
}
NgeMonacoLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoLoaderService, deps: [{ token: NGE_MONACO_CONFIG, optional: true }, { token: NGE_MONACO_CONTRIBUTION, optional: true }, { token: i1.ResourceLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
NgeMonacoLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoLoaderService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGE_MONACO_CONFIG]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGE_MONACO_CONTRIBUTION]
                    }] }, { type: i1.ResourceLoaderService }];
    } });

class NgeMonacoDiffEditorComponent {
    constructor(loader, config) {
        this.loader = loader;
        this.config = config;
        this.ready = new EventEmitter();
        this.autoLayout = true;
        this.width = 0;
        this.height = 0;
    }
    onResizeWindow() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.layout();
    }
    ngAfterViewInit() {
        this.loader.loadAsync().then(() => {
            this.createEditor();
        });
    }
    ngAfterViewChecked() {
        var _a;
        if (!this.autoLayout) {
            return;
        }
        const { offsetWidth, offsetHeight } = this.container.nativeElement;
        if (offsetWidth !== this.width || offsetHeight !== this.height) {
            this.width = offsetWidth;
            this.height = offsetHeight;
            (_a = this.editor) === null || _a === void 0 ? void 0 : _a.layout();
        }
    }
    ngOnDestroy() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    createEditor() {
        this.editor = monaco.editor.createDiffEditor(this.container.nativeElement, Object.assign(Object.assign({}, (this.config.options || {})), (this.options || {})));
        this.ready.emit(this.editor);
    }
}
NgeMonacoDiffEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoDiffEditorComponent, deps: [{ token: NgeMonacoLoaderService }, { token: NGE_MONACO_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component });
NgeMonacoDiffEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: NgeMonacoDiffEditorComponent, selector: "nge-monaco-diff-editor", inputs: { autoLayout: "autoLayout", options: "options" }, outputs: { ready: "ready" }, host: { listeners: { "window:resize": "onResizeWindow()" } }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"nge-monaco-diff-editor-container\" #container></div>\n", styles: [":host{display:block;height:var(--editor-height, 100%);border:1px solid #F5F5F5;box-sizing:border-box}.nge-monaco-diff-editor-container{width:100%;height:100%}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoDiffEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nge-monaco-diff-editor', template: "<div class=\"nge-monaco-diff-editor-container\" #container></div>\n", styles: [":host{display:block;height:var(--editor-height, 100%);border:1px solid #F5F5F5;box-sizing:border-box}.nge-monaco-diff-editor-container{width:100%;height:100%}\n"] }]
        }], ctorParameters: function () {
        return [{ type: NgeMonacoLoaderService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGE_MONACO_CONFIG]
                    }] }];
    }, propDecorators: { container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], ready: [{
                type: Output
            }], autoLayout: [{
                type: Input
            }], options: [{
                type: Input
            }], onResizeWindow: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class NgeMonacoEditorComponent {
    constructor(loader, config) {
        this.loader = loader;
        this.config = config;
        this.ready = new EventEmitter();
        this.autoLayout = true;
        this.width = 0;
        this.height = 0;
    }
    onResizeWindow() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.layout();
    }
    ngAfterViewInit() {
        this.loader.loadAsync().then(() => {
            this.createEditor();
        });
    }
    ngAfterViewChecked() {
        var _a;
        if (!this.autoLayout) {
            return;
        }
        const { offsetWidth, offsetHeight } = this.container.nativeElement;
        if (offsetWidth !== this.width || offsetHeight !== this.height) {
            this.width = offsetWidth;
            this.height = offsetHeight;
            (_a = this.editor) === null || _a === void 0 ? void 0 : _a.layout();
        }
    }
    ngOnDestroy() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    createEditor() {
        this.editor = monaco.editor.create(this.container.nativeElement, Object.assign(Object.assign({}, (this.config.options || {})), (this.options || {})));
        this.ready.emit(this.editor);
    }
}
NgeMonacoEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoEditorComponent, deps: [{ token: NgeMonacoLoaderService }, { token: NGE_MONACO_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component });
NgeMonacoEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: NgeMonacoEditorComponent, selector: "nge-monaco-editor", inputs: { autoLayout: "autoLayout", options: "options" }, outputs: { ready: "ready" }, host: { listeners: { "window:resize": "onResizeWindow()" } }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"nge-monaco-editor-container\" #container></div>\n", styles: [":host{display:block;height:var(--editor-height, 100%);border:1px solid #F5F5F5;box-sizing:border-box}.nge-monaco-editor-container{width:100%;height:100%}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nge-monaco-editor', template: "<div class=\"nge-monaco-editor-container\" #container></div>\n", styles: [":host{display:block;height:var(--editor-height, 100%);border:1px solid #F5F5F5;box-sizing:border-box}.nge-monaco-editor-container{width:100%;height:100%}\n"] }]
        }], ctorParameters: function () {
        return [{ type: NgeMonacoLoaderService }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGE_MONACO_CONFIG]
                    }] }];
    }, propDecorators: { container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], ready: [{
                type: Output
            }], autoLayout: [{
                type: Input
            }], options: [{
                type: Input
            }], onResizeWindow: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class NgeMonacoThemeService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.themes = new BehaviorSubject([]);
        this.activeTheme = new BehaviorSubject(undefined);
    }
    /**
     * Gets the current active theme of monaco editor (undefined if monaco editor is not loaded).
     */
    get theme() {
        return this.activeTheme.value;
    }
    /**
     * Gets an observable that emit each time monaco editor theme change.
     *
     * Note: The observable emits first with the current theme
     * the first time `subscribe()` method is called.
     */
    get themeChanges() {
        return this.activeTheme.asObservable();
    }
    /**
     * Gets an observable that emit each time monaco editor theme list change.
     */
    get themesChanges() {
        return this.themes.asObservable().pipe(map((e) => e.slice()) // return a copy of the array
        );
    }
    activate() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const node = document.createElement('div');
            const editor = monaco.editor.create(node);
            this.themeService = editor._themeService;
            setTimeout(() => editor.dispose());
            this.retrieveThemes();
            yield this.setTheme(((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.theming) === null || _b === void 0 ? void 0 : _b.default) || 'vs');
        });
    }
    /**
     * Switches monaco editor theme.
     * @param themeName The new theme to use.
     *
     */
    setTheme(themeName) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.defineTheme(themeName);
            monaco.editor.setTheme(themeName);
            this.activeTheme.next(this.themeService.getColorTheme());
        });
    }
    /**
     * Gets the information about the given `themeName`
     * @param themeName The theme to get.
     * @returns A promise that resolves with the theme info.
     */
    getTheme(themeName) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.defineTheme(themeName);
            return this.themeService._knownThemes.get(themeName);
        });
    }
    retrieveThemes() {
        var _a, _b;
        const themes = [];
        this.themeService._knownThemes.forEach((theme) => {
            themes.push(theme.themeName);
        });
        const customThemes = (((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.theming) === null || _b === void 0 ? void 0 : _b.themes) || []).map(this.themeNameFromPath.bind(this));
        this.themes.next(themes.concat(customThemes));
    }
    themeNameFromPath(path) {
        const name = path.split('/').pop();
        if (!name) {
            throw new Error(`[nge-monaco]: invalid theme path "${path}"`);
        }
        return name.replace('.json', '');
    }
    defineTheme(themeName) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (!themeName) {
                throw new ReferenceError('Argument "themeName" is required');
            }
            const knownThemes = this.themeService
                ._knownThemes;
            if (knownThemes.has(themeName)) {
                return;
            }
            const customThemePath = (_c = (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.theming) === null || _b === void 0 ? void 0 : _b.themes) === null || _c === void 0 ? void 0 : _c.find((e) => {
                return this.themeNameFromPath(e) === themeName;
            });
            if (!customThemePath) {
                throw new Error(`[nge-monaco] Missing theme "${themeName}"`);
            }
            if (!this.http) {
                throw new Error('[nge-monaco] Missing HttpClientModule in AppModule. See README for more information');
            }
            try {
                const theme = yield lastValueFrom(this.http.get(customThemePath));
                monaco.editor.defineTheme(themeName, {
                    base: theme.base,
                    inherit: theme.inherit,
                    rules: theme.rules,
                    colors: theme.colors,
                });
            }
            catch (error) {
                console.error('[nge-monaco] Failed to load theme ' + customThemePath, error);
            }
        });
    }
}
NgeMonacoThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoThemeService, deps: [{ token: i1$1.HttpClient, optional: true }, { token: NGE_MONACO_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
NgeMonacoThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoThemeService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: i1$1.HttpClient, decorators: [{
                        type: Optional
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [NGE_MONACO_CONFIG]
                    }] }];
    } });
/** List of all custom themes from of the library */
const NGE_MONACO_THEMES = [
    'active4d.json',
    'all-hallows-eve.json',
    'amy.json',
    'birds-of-paradise.json',
    'blackboard.json',
    'brilliance-black.json',
    'brilliance-dull.json',
    'chrome-devtools.json',
    'clouds-midnight.json',
    'clouds.json',
    'cobalt.json',
    'dawn.json',
    'dreamweaver.json',
    'eiffel.json',
    'espresso-libre.json',
    'github.json',
    'idle-fingers.json',
    'idle.json',
    'iplastic.json',
    'katzenmilch.json',
    'kuroir-theme.json',
    'kr-theme.json',
    'lazy.json',
    'magicwb-amiga.json',
    'merbivore-soft.json',
    'merbivore.json',
    'monokai-bright.json',
    'monokai.json',
    'monoindustrial.json',
    'night-owl.json',
    'nord.json',
    'oceanic-next.json',
    'one-dark-pro.json',
    'pastels-on-dark.json',
    'slush-and-poppies.json',
    'solarized-dark.json',
    'solarized-light.json',
    'space-cadet.json',
    'sunburst.json',
    'textmate.json',
    'tomorrow-night-blue.json',
    'tomorrow-night-bright.json',
    'tomorrow-night-eighties.json',
    'tomorrow-night.json',
    'tomorrow.json',
    'twilight.json',
    'upstream-sunburst.json',
    'vibrant-ink.json',
    'xcode.json',
    'zenburnesque.json',
];

class NgeMonacoColorizerService {
    constructor(loader, theming) {
        this.loader = loader;
        this.theming = theming;
    }
    colorizeElement(options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { element } = options;
            element.innerHTML = this.escapeHtml(options.code || '');
            element.style.padding = '4px';
            element.style.display = 'block';
            const pre = element.parentElement;
            if ((pre === null || pre === void 0 ? void 0 : pre.tagName) === 'PRE') {
                if (!pre.classList.contains('monaco-editor')) {
                    pre.classList.add('monaco-editor');
                }
                if (!pre.classList.contains('monaco-editor-background')) {
                    pre.classList.add('monaco-editor-background');
                }
            }
            element.className = '';
            yield this.loader.loadAsync();
            const languages = monaco.languages.getLanguages();
            const language = (_a = languages.find(e => {
                var _a;
                return (e.id === options.language) || ((_a = e.aliases) === null || _a === void 0 ? void 0 : _a.find(a => a === options.language));
            })) === null || _a === void 0 ? void 0 : _a.id;
            yield monaco.editor.colorizeElement(element, {
                mimeType: language || 'plaintext',
                theme: ((_b = this.theming.theme) === null || _b === void 0 ? void 0 : _b.themeName) || 'vs',
            });
            this.highlightLines(options);
            this.showLineNumbers(options);
        });
    }
    escapeHtml(input) {
        const map = {
            '<': '&lt;',
            '>': '&gt;'
        };
        return input.replace(/[<>]/g, (tag) => map[tag] || tag);
    }
    highlightLines(options) {
        if (!options.highlights) {
            return;
        }
        const { element } = options;
        const linesToHighlight = this.lineNumbersFromString(options.highlights.toString());
        let newLine = true;
        let lineNumber = 1;
        element.childNodes.forEach((e) => {
            const node = e;
            if (newLine) {
                const div = document.createElement('div');
                div.style.height = '18px';
                if (linesToHighlight.includes(lineNumber)) {
                    div.classList.add('rangeHighlight');
                    div.classList.add('selected-text');
                }
                element.insertBefore(div, node);
                element.removeChild(node);
                div.appendChild(node);
                newLine = false;
            }
            else if (node.tagName === 'BR') {
                lineNumber++;
                newLine = true;
            }
        });
        Array.from(element.getElementsByTagName('br')).forEach(node => node.remove());
    }
    showLineNumbers(options) {
        if (!options.lines) {
            return;
        }
        const { element } = options;
        const lines = this.lineNumbersFromString(options.lines.toString());
        const length = (options.code || '').split('\n').length;
        if (lines.length === 1) {
            for (let i = lines[0] + 1; i <= length; i++) {
                lines.push(i);
            }
        }
        const side = ['<div style="padding:0  12px; text-align: right;">'];
        for (let i = 0; i < length; i++) {
            let num = '';
            if (lines.includes(i + 1)) {
                num = '' + (i + 1);
            }
            side.push(`<div class="line-numbers" style="height: 18px">${num}</div>`);
        }
        side.push('</div>');
        element.style.display = 'flex';
        element.innerHTML = `
            ${side.join('')}
            <div style="flex: 1;">${element.innerHTML}</div>
        `;
    }
    lineNumbersFromString(input) {
        const tokens = (input || '')
            .trim()
            .split(' ');
        const lines = [];
        for (const token of tokens) {
            if (token.includes('-')) {
                const range = token.split('-');
                const start = Number.parseInt(range[0], 10);
                const end = Number.parseInt(range[1], 10);
                if (start && end) {
                    for (let i = start; i <= end; i++) {
                        if (!lines.includes(i)) {
                            lines.push(i);
                        }
                    }
                }
            }
            else {
                const n = Number.parseInt(token, 10);
                if (n) {
                    lines.push(n);
                }
            }
        }
        return lines;
    }
}
NgeMonacoColorizerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoColorizerService, deps: [{ token: NgeMonacoLoaderService }, { token: NgeMonacoThemeService }], target: i0.ɵɵFactoryTarget.Injectable });
NgeMonacoColorizerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoColorizerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoColorizerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: NgeMonacoLoaderService }, { type: NgeMonacoThemeService }]; } });

class NgeMonacoViewerComponent {
    constructor(colorizer) {
        this.colorizer = colorizer;
        this.subscriptions = [];
    }
    ngOnChanges() {
        var _a;
        const code = ((_a = this
            .transclusion
            .nativeElement
            .textContent) === null || _a === void 0 ? void 0 : _a.trim())
            || this.code || '';
        this.colorize(code);
    }
    ngOnDestroy() {
        var _a, _b;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.dispose();
        (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.subscriptions.forEach(s => s.unsubscribe());
    }
    colorize(code) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.colorizer.colorizeElement({
                code: code || '',
                element: this.container.nativeElement,
                lines: this.lines,
                language: this.language,
                highlights: this.highlights,
            });
        });
    }
}
NgeMonacoViewerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoViewerComponent, deps: [{ token: NgeMonacoColorizerService }], target: i0.ɵɵFactoryTarget.Component });
NgeMonacoViewerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.3", type: NgeMonacoViewerComponent, selector: "nge-monaco-viewer", inputs: { code: "code", lines: "lines", language: "language", highlights: "highlights" }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }, { propertyName: "transclusion", first: true, predicate: ["transclusion"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<pre class=\"monaco-editor monaco-editor-background\">\n  <code #container></code>\n</pre>\n\n<div #transclusion style=\"display: none;\">\n  <ng-content></ng-content>\n</div>\n\n", styles: ["pre{margin:.5em 0;overflow:auto;border:1px solid #F2F2F2}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nge-monaco-viewer', changeDetection: ChangeDetectionStrategy.OnPush, template: "<pre class=\"monaco-editor monaco-editor-background\">\n  <code #container></code>\n</pre>\n\n<div #transclusion style=\"display: none;\">\n  <ng-content></ng-content>\n</div>\n\n", styles: ["pre{margin:.5em 0;overflow:auto;border:1px solid #F2F2F2}\n"] }]
        }], ctorParameters: function () { return [{ type: NgeMonacoColorizerService }]; }, propDecorators: { container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], transclusion: [{
                type: ViewChild,
                args: ['transclusion', { static: true }]
            }], code: [{
                type: Input
            }], lines: [{
                type: Input
            }], language: [{
                type: Input
            }], highlights: [{
                type: Input
            }] } });

// ACTIONS
/** Toggle High Contrast Theme */
const ACTION_TOGGLE_HIGH_CONTRAST = 'editor.action.toggleHighContrast';
/** Set Selection Anchor */
const ACTION_SET_SELECTION_ANCHOR = 'editor.action.setSelectionAnchor';
/** Move Selected Text Left */
const ACTION_MOVE_CARRET_LEFT = 'editor.action.moveCarretLeftAction';
/** Move Selected Text Right */
const ACTION_MOVE_CARRET_RIGHT = 'editor.action.moveCarretRightAction';
/** Transpose Letters */
const ACTION_TRANSPOSE_LETTERS = 'editor.action.transposeLetters';
/** Copy With Syntax Highlighting */
const ACTION_CLIPBOARD_COPY_WITH_SYNTAX_HIGHLIGHTING = 'editor.action.clipboardCopyWithSyntaxHighlightingAction';
/** Toggle Line Comment */
const ACTION_COMMENT_LINE = 'editor.action.commentLine';
/** Add Line Comment */
const ACTION_ADD_COMMENT_LINE = 'editor.action.addCommentLine';
/** Remove Line Comment */
const ACTION_REMOVE_COMMENT_LINE = 'editor.action.removeCommentLine';
/** Toggle Block Comment */
const ACTION_BLOCK_COMMENT = 'editor.action.blockComment';
/** Show Editor Context Menu */
const ACTION_SHOW_CONTEXT_MENU = 'editor.action.showContextMenu';
/** Cursor Undo */
const ACTION_CURSOR_UNDO = 'cursorUndo';
/** Cursor Redo */
const ACTION_CURSOR_REDO = 'cursorRedo';
/** Editor Font Zoom In */
const ACTION_FONT_ZOOM_IN = 'editor.action.fontZoomIn';
/** Editor Font Zoom Out */
const ACTION_FONT_ZOOM_OUT = 'editor.action.fontZoomOut';
/** Editor Font Zoom Reset */
const ACTION_FONT_ZOOM_RESET = 'editor.action.fontZoomReset';
/** Convert Indentation to Spaces */
const ACTION_INDENTATION_TO_SPACES = 'editor.action.indentationToSpaces';
/** Convert Indentation to Tabs */
const ACTION_INDENTATION_TO_TABS = 'editor.action.indentationToTabs';
/** Indent Using Tabs */
const ACTION_INDENT_USING_TABS = 'editor.action.indentUsingTabs';
/** Indent Using Spaces */
const ACTION_INDENT_USING_SPACES = 'editor.action.indentUsingSpaces';
/** Detect Indentation from Content */
const ACTION_DETECT_INDENTATION = 'editor.action.detectIndentation';
/** Reindent Lines */
const ACTION_REINDENTLINES = 'editor.action.reindentlines';
/** Reindent Selected Lines */
const ACTION_REINDENTSELECTEDLINES = 'editor.action.reindentselectedlines';
/** Copy Line Up */
const ACTION_COPY_LINES_UP = 'editor.action.copyLinesUpAction';
/** Copy Line Down */
const ACTION_COPY_LINES_DOWN = 'editor.action.copyLinesDownAction';
/** Duplicate Selection */
const ACTION_DUPLICATE_SELECTION = 'editor.action.duplicateSelection';
/** Move Line Up */
const ACTION_MOVE_LINES_UP = 'editor.action.moveLinesUpAction';
/** Move Line Down */
const ACTION_MOVE_LINES_DOWN = 'editor.action.moveLinesDownAction';
/** Sort Lines Ascending */
const ACTION_SORT_LINES_ASCENDING = 'editor.action.sortLinesAscending';
/** Sort Lines Descending */
const ACTION_SORT_LINES_DESCENDING = 'editor.action.sortLinesDescending';
/** Trim Trailing Whitespace */
const ACTION_TRIM_TRAILING_WHITESPACE = 'editor.action.trimTrailingWhitespace';
/** Delete Line */
const ACTION_DELETE_LINES = 'editor.action.deleteLines';
/** Indent Line */
const ACTION_INDENT_LINES = 'editor.action.indentLines';
/** Outdent Line */
const ACTION_OUTDENT_LINES = 'editor.action.outdentLines';
/** Insert Line Above */
const ACTION_INSERT_LINE_BEFORE = 'editor.action.insertLineBefore';
/** Insert Line Below */
const ACTION_INSERT_LINE_AFTER = 'editor.action.insertLineAfter';
/** Delete All Left */
const ACTION_DELETE_ALL_LEFT = 'deleteAllLeft';
/** Delete All Right */
const ACTION_DELETE_ALL_RIGHT = 'deleteAllRight';
/** Join Lines */
const ACTION_JOIN_LINES = 'editor.action.joinLines';
/** Transpose characters around the cursor */
const ACTION_TRANSPOSE = 'editor.action.transpose';
/** Transform to Uppercase */
const ACTION_TRANSFORM_TO_UPPERCASE = 'editor.action.transformToUppercase';
/** Transform to Lowercase */
const ACTION_TRANSFORM_TO_LOWERCASE = 'editor.action.transformToLowercase';
/** Transform to Title Case */
const ACTION_TRANSFORM_TO_TITLECASE = 'editor.action.transformToTitlecase';
/** Expand Selection */
const ACTION_SMART_SELECT_EXPAND = 'editor.action.smartSelect.expand';
/** Shrink Selection */
const ACTION_SMART_SELECT_SHRINK = 'editor.action.smartSelect.shrink';
/** Developer: Force Retokenize */
const ACTION_FORCE_RETOKENIZE = 'editor.action.forceRetokenize';
/** Toggle Tab Key Moves Focus */
const ACTION_TOGGLE_TAB_FOCUS_MODE = 'editor.action.toggleTabFocusMode';
/** Command Palette */
const ACTION_QUICK_COMMAND = 'editor.action.quickCommand';
/** Replace with Previous Value */
const ACTION_IN_PLACE_REPLACE_UP = 'editor.action.inPlaceReplace.up';
/** Replace with Next Value */
const ACTION_IN_PLACE_REPLACE_DOWN = 'editor.action.inPlaceReplace.down';
/** Go to Line/Column... */
const ACTION_GOTO_LINE = 'editor.action.gotoLine';
/** Select to Bracket */
const ACTION_SELECT_TO_BRACKET = 'editor.action.selectToBracket';
/** Go to Bracket */
const ACTION_JUMP_TO_BRACKET = 'editor.action.jumpToBracket';
/** Find */
const ACTION_FIND = 'actions.find';
/** Find With Selection */
const ACTION_FIND_WITH_SELECTION = 'actions.findWithSelection';
/** Find Next */
const ACTION_NEXT_MATCH_FIND = 'editor.action.nextMatchFindAction';
/** Find Previous */
const ACTION_PREVIOUS_MATCH_FIND = 'editor.action.previousMatchFindAction';
/** Find Next Selection */
const ACTION_NEXT_SELECTION_MATCH_FIND = 'editor.action.nextSelectionMatchFindAction';
/** Find Previous Selection */
const ACTION_PREVIOUS_SELECTION_MATCH_FIND = 'editor.action.previousSelectionMatchFindAction';
/** Replace */
const ACTION_START_FIND_REPLACE = 'editor.action.startFindReplaceAction';
/** Unfold */
const ACTION_EDITOR_UNFOLD = 'editor.unfold';
/** Unfold Recursively */
const ACTION_EDITOR_UNFOLD_RECURSIVELY = 'editor.unfoldRecursively';
/** Fold */
const ACTION_EDITOR_FOLD = 'editor.fold';
/** Fold Recursively */
const ACTION_EDITOR_FOLD_RECURSIVELY = 'editor.foldRecursively';
/** Fold All */
const ACTION_EDITOR_FOLD_ALL = 'editor.foldAll';
/** Unfold All */
const ACTION_EDITOR_UNFOLD_ALL = 'editor.unfoldAll';
/** Fold All Block Comments */
const ACTION_EDITOR_FOLD_ALL_BLOCK_COMMENTS = 'editor.foldAllBlockComments';
/** Fold All Regions */
const ACTION_EDITOR_FOLD_ALL_MARKER_REGIONS = 'editor.foldAllMarkerRegions';
/** Unfold All Regions */
const ACTION_EDITOR_UNFOLD_ALL_MARKER_REGIONS = 'editor.unfoldAllMarkerRegions';
/** Toggle Fold */
const ACTION_EDITOR_TOGGLE_FOLD = 'editor.toggleFold';
/** Fold Level 1 */
const ACTION_EDITOR_FOLD_LEVEL1 = 'editor.foldLevel1';
/** Fold Level 2 */
const ACTION_EDITOR_FOLD_LEVEL2 = 'editor.foldLevel2';
/** Fold Level 3 */
const ACTION_EDITOR_FOLD_LEVEL3 = 'editor.foldLevel3';
/** Fold Level 4 */
const ACTION_EDITOR_FOLD_LEVEL4 = 'editor.foldLevel4';
/** Fold Level 5 */
const ACTION_EDITOR_FOLD_LEVEL5 = 'editor.foldLevel5';
/** Fold Level 6 */
const ACTION_EDITOR_FOLD_LEVEL6 = 'editor.foldLevel6';
/** Fold Level 7 */
const ACTION_EDITOR_FOLD_LEVEL7 = 'editor.foldLevel7';
/** Open Link */
const ACTION_OPEN_LINK = 'editor.action.openLink';
/** Trigger Symbol Highlight */
const ACTION_WORD_HIGHLIGHT_TRIGGER = 'editor.action.wordHighlight.trigger';
/** Show Accessibility Help */
const ACTION_SHOW_ACCESSIBILITY_HELP = 'editor.action.showAccessibilityHelp';
/** Developer: Inspect Tokens */
const ACTION_INSPECT_TOKENS = 'editor.action.inspectTokens';
/** Go to Next Problem (Error, Warning, Info) */
const ACTION_MARKER_NEXT = 'editor.action.marker.next';
/** Go to Previous Problem (Error, Warning, Info) */
const ACTION_MARKER_PREV = 'editor.action.marker.prev';
/** Go to Next Problem in Files (Error, Warning, Info) */
const ACTION_MARKER_NEXT_IN_FILES = 'editor.action.marker.nextInFiles';
/** Go to Previous Problem in Files (Error, Warning, Info) */
const ACTION_MARKER_PREV_IN_FILES = 'editor.action.marker.prevInFiles';
/** Show Hover */
const ACTION_SHOW_HOVER = 'editor.action.showHover';
/** Show Definition Preview Hover */
const ACTION_SHOW_DEFINITION_PREVIEW_HOVER = 'editor.action.showDefinitionPreviewHover';
/** Add Cursor Above */
const ACTION_INSERT_CURSOR_ABOVE = 'editor.action.insertCursorAbove';
/** Add Cursor Below */
const ACTION_INSERT_CURSOR_BELOW = 'editor.action.insertCursorBelow';
/** Add Cursors to Line Ends */
const ACTION_INSERT_CURSOR_AT_END_OF_EACH_LINE_SELECTED = 'editor.action.insertCursorAtEndOfEachLineSelected';
/** Add Selection To Next Find Match */
const ACTION_ADD_SELECTION_TO_NEXT_FIND_MATCH = 'editor.action.addSelectionToNextFindMatch';
/** Add Selection To Previous Find Match */
const ACTION_ADD_SELECTION_TO_PREVIOUS_FIND_MATCH = 'editor.action.addSelectionToPreviousFindMatch';
/** Move Last Selection To Next Find Match */
const ACTION_MOVE_SELECTION_TO_NEXT_FIND_MATCH = 'editor.action.moveSelectionToNextFindMatch';
/** Move Last Selection To Previous Find Match */
const ACTION_MOVE_SELECTION_TO_PREVIOUS_FIND_MATCH = 'editor.action.moveSelectionToPreviousFindMatch';
/** Select All Occurrences of Find Match */
const ACTION_SELECT_HIGHLIGHTS = 'editor.action.selectHighlights';
/** Add Cursors To Bottom */
const ACTION_ADD_CURSORS_TO_BOTTOM = 'editor.action.addCursorsToBottom';
/** Add Cursors To Top */
const ACTION_ADD_CURSORS_TO_TOP = 'editor.action.addCursorsToTop';
/** Trigger Suggest */
const ACTION_TRIGGER_SUGGEST = 'editor.action.triggerSuggest';
// https://github.com/microsoft/vscode/tree/master/src/vs/editor/contrib
const COLOR_DETECTOR_CONTRIB = 'editor.contrib.colorDetector';
const CONTEXT_MENU_CONTRIB = 'editor.contrib.contextmenu';
const CURSOR_UNDO_REDO_CONTROLLER_CONTRIB = 'editor.contrib.cursorUndoRedoController';
const DRAG_AND_DROP_CONTRIB = 'editor.contrib.dragAndDrop';
const AUTO_FORMAT_CONTRIB = 'editor.contrib.autoFormat';
const FORMAT_ON_PAST_CONTRIB = 'editor.contrib.formatOnPaste';
const SMART_SELECT_CONTRIB = 'editor.contrib.smartSelectController';
const IPAD_SHOW_KEYBOARD_CONTRIB = 'editor.contrib.iPadShowKeyboard';
const BRACKET_MATCHING_CONTROLLER_CONTRIB = 'editor.contrib.bracketMatchingController';
const CODE_LENS_CONTRIB = 'css.editor.codeLens';
const FIND_CONTROLLE_CONTRIB = 'editor.contrib.findController';
const FOLDING_CONTRIB = 'editor.contrib.folding';
const IN_PLACE_REPLACE_CONTROLLER_CONTRIB = 'editor.contrib.inPlaceReplaceController';
const LINK_DETECTOR_CONTRIB = 'editor.linkDetector';
const MESSAGE_CONTROLLER_CONTRIB = 'editor.contrib.messageController';
const QUICK_FIX_CONTROLLER_CONTRIB = 'editor.contrib.quickFixController';
const MULTI_CURSOR_CONTROLLER_CONTRIB = 'editor.contrib.multiCursorController';
const SELECTION_HIGHLIGHTER_CONTRIB = 'editor.contrib.selectionHighlighter';
const PARAMETER_HINTS_CONTRIB = 'editor.controller.parameterHints';
const REFERENCE_CONTROLLER_CONTRIB = 'editor.contrib.referenceController';
const RENAME_CONTROLLER_CONTRIB = 'editor.contrib.renameController';
const WORD_HIGHLIGHTER_CONTRIB = 'editor.contrib.wordHighlighter';
const ACCESSIBILIY_HELP_CONTROLLER_CONTRIB = 'editor.contrib.accessibilityHelpController';
const INSPECT_TOKENS_CONTRIB = 'editor.contrib.inspectTokens';
const QUICK_OPEN_CONTROLLER_CONTRIB = 'editor.controller.quickOpenController';
const GOTO_DEFINITION_CONTRIB = 'editor.contrib.gotodefinitionatposition';
const REFERENCES_CONTROLLER_CONTRIB = 'editor.contrib.referencesController';
const MARKER_CONTROLLER_CONTRIB = 'editor.contrib.markerController';
const HOVER_CONTRIB = 'editor.contrib.hover';
const SNIPPET_CONTROLLER_CONTRIB = 'snippetController2';
const SUGGEST_CONTROLLER_CONTRIB = 'editor.contrib.suggestController';

/**
 * When a user type a composition key like ^ or \` the editor
 * enter in composition mode, then after the user type any key the editor leave
 * this composition mode an duplicate the character pressed by the user.
 *
 * This bug occurs only on some browsers like firefox and calling this method will prevent this behavior
 * by removing any extra character added by the editor between 2 calls
 * of `onDidCompositionStart` and `onDidCompositionEnd`
 */
class PreventSymbolDuplication {
    activate() {
        this.disposable = monaco.editor.onDidCreateEditor((e) => {
            this.preventSymbolDuplicationOnCompositionEnd(e);
        });
    }
    deactivate() {
        var _a;
        (_a = this.disposable) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    preventSymbolDuplicationOnCompositionEnd(editor) {
        const positions = [];
        let disposables = [];
        disposables.push(editor.onDidCompositionStart(() => {
            const position = editor.getPosition();
            if (position) {
                positions.push(position);
            }
        }));
        disposables.push(editor.onDidCompositionEnd(() => {
            setTimeout(() => {
                var _a;
                if (!positions.length) {
                    return;
                }
                const before = positions[0];
                const after = editor.getPosition();
                if (!after) {
                    return;
                }
                positions.splice(0, 1);
                const diff = after.column - before.column;
                if (diff > 1) {
                    // unfocus the editor to leave composition
                    // mode because when the user type ` the editor
                    // leave the composition mode and begin another one
                    (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur();
                    // focus the editor to let the user continue to edit the content
                    // of the editor
                    editor.focus();
                    const r = new monaco.Range(after.lineNumber, after.column - (diff - 1), after.lineNumber, after.column);
                    editor.executeEdits('api', [{ range: r, text: '', forceMoveMarkers: false }]);
                }
            });
        }));
        disposables.push(editor.onDidDispose(() => {
            disposables.forEach(e => e.dispose());
            disposables = [];
        }));
    }
}

class NgeMonacoModule {
    static forRoot(config) {
        return {
            ngModule: NgeMonacoModule,
            providers: [
                { provide: NGE_MONACO_CONFIG, useValue: config },
                { provide: NGE_MONACO_CONTRIBUTION, multi: true, useExisting: NgeMonacoThemeService },
                { provide: NGE_MONACO_CONTRIBUTION, multi: true, useClass: PreventSymbolDuplication }
            ]
        };
    }
}
NgeMonacoModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgeMonacoModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoModule, declarations: [NgeMonacoEditorComponent,
        NgeMonacoDiffEditorComponent,
        NgeMonacoViewerComponent], imports: [CommonModule], exports: [NgeMonacoEditorComponent,
        NgeMonacoDiffEditorComponent,
        NgeMonacoViewerComponent] });
NgeMonacoModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoModule, imports: [[
            CommonModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.3", ngImport: i0, type: NgeMonacoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        NgeMonacoEditorComponent,
                        NgeMonacoDiffEditorComponent,
                        NgeMonacoViewerComponent
                    ],
                    declarations: [
                        NgeMonacoEditorComponent,
                        NgeMonacoDiffEditorComponent,
                        NgeMonacoViewerComponent
                    ],
                }]
        }] });

// tslint:disable-next-line: no-reference

/**
 * Generated bundle index. Do not edit.
 */

export { ACCESSIBILIY_HELP_CONTROLLER_CONTRIB, ACTION_ADD_COMMENT_LINE, ACTION_ADD_CURSORS_TO_BOTTOM, ACTION_ADD_CURSORS_TO_TOP, ACTION_ADD_SELECTION_TO_NEXT_FIND_MATCH, ACTION_ADD_SELECTION_TO_PREVIOUS_FIND_MATCH, ACTION_BLOCK_COMMENT, ACTION_CLIPBOARD_COPY_WITH_SYNTAX_HIGHLIGHTING, ACTION_COMMENT_LINE, ACTION_COPY_LINES_DOWN, ACTION_COPY_LINES_UP, ACTION_CURSOR_REDO, ACTION_CURSOR_UNDO, ACTION_DELETE_ALL_LEFT, ACTION_DELETE_ALL_RIGHT, ACTION_DELETE_LINES, ACTION_DETECT_INDENTATION, ACTION_DUPLICATE_SELECTION, ACTION_EDITOR_FOLD, ACTION_EDITOR_FOLD_ALL, ACTION_EDITOR_FOLD_ALL_BLOCK_COMMENTS, ACTION_EDITOR_FOLD_ALL_MARKER_REGIONS, ACTION_EDITOR_FOLD_LEVEL1, ACTION_EDITOR_FOLD_LEVEL2, ACTION_EDITOR_FOLD_LEVEL3, ACTION_EDITOR_FOLD_LEVEL4, ACTION_EDITOR_FOLD_LEVEL5, ACTION_EDITOR_FOLD_LEVEL6, ACTION_EDITOR_FOLD_LEVEL7, ACTION_EDITOR_FOLD_RECURSIVELY, ACTION_EDITOR_TOGGLE_FOLD, ACTION_EDITOR_UNFOLD, ACTION_EDITOR_UNFOLD_ALL, ACTION_EDITOR_UNFOLD_ALL_MARKER_REGIONS, ACTION_EDITOR_UNFOLD_RECURSIVELY, ACTION_FIND, ACTION_FIND_WITH_SELECTION, ACTION_FONT_ZOOM_IN, ACTION_FONT_ZOOM_OUT, ACTION_FONT_ZOOM_RESET, ACTION_FORCE_RETOKENIZE, ACTION_GOTO_LINE, ACTION_INDENTATION_TO_SPACES, ACTION_INDENTATION_TO_TABS, ACTION_INDENT_LINES, ACTION_INDENT_USING_SPACES, ACTION_INDENT_USING_TABS, ACTION_INSERT_CURSOR_ABOVE, ACTION_INSERT_CURSOR_AT_END_OF_EACH_LINE_SELECTED, ACTION_INSERT_CURSOR_BELOW, ACTION_INSERT_LINE_AFTER, ACTION_INSERT_LINE_BEFORE, ACTION_INSPECT_TOKENS, ACTION_IN_PLACE_REPLACE_DOWN, ACTION_IN_PLACE_REPLACE_UP, ACTION_JOIN_LINES, ACTION_JUMP_TO_BRACKET, ACTION_MARKER_NEXT, ACTION_MARKER_NEXT_IN_FILES, ACTION_MARKER_PREV, ACTION_MARKER_PREV_IN_FILES, ACTION_MOVE_CARRET_LEFT, ACTION_MOVE_CARRET_RIGHT, ACTION_MOVE_LINES_DOWN, ACTION_MOVE_LINES_UP, ACTION_MOVE_SELECTION_TO_NEXT_FIND_MATCH, ACTION_MOVE_SELECTION_TO_PREVIOUS_FIND_MATCH, ACTION_NEXT_MATCH_FIND, ACTION_NEXT_SELECTION_MATCH_FIND, ACTION_OPEN_LINK, ACTION_OUTDENT_LINES, ACTION_PREVIOUS_MATCH_FIND, ACTION_PREVIOUS_SELECTION_MATCH_FIND, ACTION_QUICK_COMMAND, ACTION_REINDENTLINES, ACTION_REINDENTSELECTEDLINES, ACTION_REMOVE_COMMENT_LINE, ACTION_SELECT_HIGHLIGHTS, ACTION_SELECT_TO_BRACKET, ACTION_SET_SELECTION_ANCHOR, ACTION_SHOW_ACCESSIBILITY_HELP, ACTION_SHOW_CONTEXT_MENU, ACTION_SHOW_DEFINITION_PREVIEW_HOVER, ACTION_SHOW_HOVER, ACTION_SMART_SELECT_EXPAND, ACTION_SMART_SELECT_SHRINK, ACTION_SORT_LINES_ASCENDING, ACTION_SORT_LINES_DESCENDING, ACTION_START_FIND_REPLACE, ACTION_TOGGLE_HIGH_CONTRAST, ACTION_TOGGLE_TAB_FOCUS_MODE, ACTION_TRANSFORM_TO_LOWERCASE, ACTION_TRANSFORM_TO_TITLECASE, ACTION_TRANSFORM_TO_UPPERCASE, ACTION_TRANSPOSE, ACTION_TRANSPOSE_LETTERS, ACTION_TRIGGER_SUGGEST, ACTION_TRIM_TRAILING_WHITESPACE, ACTION_WORD_HIGHLIGHT_TRIGGER, AUTO_FORMAT_CONTRIB, BRACKET_MATCHING_CONTROLLER_CONTRIB, CODE_LENS_CONTRIB, COLOR_DETECTOR_CONTRIB, CONTEXT_MENU_CONTRIB, CURSOR_UNDO_REDO_CONTROLLER_CONTRIB, DRAG_AND_DROP_CONTRIB, FIND_CONTROLLE_CONTRIB, FOLDING_CONTRIB, FORMAT_ON_PAST_CONTRIB, GOTO_DEFINITION_CONTRIB, HOVER_CONTRIB, INSPECT_TOKENS_CONTRIB, IN_PLACE_REPLACE_CONTROLLER_CONTRIB, IPAD_SHOW_KEYBOARD_CONTRIB, LINK_DETECTOR_CONTRIB, MARKER_CONTROLLER_CONTRIB, MESSAGE_CONTROLLER_CONTRIB, MONACO_CDNJS_URL, MONACO_JS_DELIVER_URL, MULTI_CURSOR_CONTROLLER_CONTRIB, NGE_MONACO_CONTRIBUTION, NGE_MONACO_THEMES, NgeMonacoColorizerService, NgeMonacoDiffEditorComponent, NgeMonacoEditorComponent, NgeMonacoLoaderService, NgeMonacoModule, NgeMonacoThemeService, NgeMonacoViewerComponent, PARAMETER_HINTS_CONTRIB, QUICK_FIX_CONTROLLER_CONTRIB, QUICK_OPEN_CONTROLLER_CONTRIB, REFERENCES_CONTROLLER_CONTRIB, REFERENCE_CONTROLLER_CONTRIB, RENAME_CONTROLLER_CONTRIB, SELECTION_HIGHLIGHTER_CONTRIB, SMART_SELECT_CONTRIB, SNIPPET_CONTROLLER_CONTRIB, SUGGEST_CONTROLLER_CONTRIB, WORD_HIGHLIGHTER_CONTRIB };
